import React from 'react';
import { TableColumn } from '../../utils/tableUtils';
import { Reminder, ReminderPriority } from '../../types/Reminder';
import { StatusBadge } from '../shared/AdminTable';

export const reminderColumns: TableColumn<Reminder>[] = [
  {
    id: 'title',
    label: 'Title',
    accessor: (reminder) => (
      <div className="flex items-center space-x-2">
        <span className={`w-2 h-2 rounded-full ${
          reminder.priority === ReminderPriority.URGENT ? 'bg-red-500' :
          reminder.priority === ReminderPriority.HIGH ? 'bg-orange-500' :
          reminder.priority === ReminderPriority.MEDIUM ? 'bg-yellow-500' :
          'bg-green-500'
        }`}></span>
        <span className={reminder.completed ? 'line-through text-gray-500' : ''}>
          {reminder.title}
        </span>
      </div>
    ),
    sortable: true,
  },
  {
    id: 'dueDateTime',
    label: 'Due Date',
    accessor: (reminder) => {
      const dueDate = new Date(reminder.dueDateTime);
      const now = new Date();
      const isOverdue = dueDate < now && !reminder.completed;

      return (
        <span className={isOverdue ? 'text-red-600 font-semibold' : ''}>
          {dueDate.toLocaleDateString()} {dueDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </span>
      );
    },
    sortable: true,
  },
  {
    id: 'category',
    label: 'Category',
    accessor: (reminder) => (
      <span className="px-2 py-1 bg-gray-100 rounded-full text-xs text-gray-700">
        {reminder.category || 'Uncategorized'}
      </span>
    ),
    sortable: true,
  },
  {
    id: 'priority',
    label: 'Priority',
    accessor: (reminder) => (
      <StatusBadge
        status={reminder.priority}
        type="priority"
      />
    ),
    sortable: true,
  },
  {
    id: 'completed',
    label: 'Status',
    accessor: (reminder) => (
      <StatusBadge
        status={reminder.completed ? 'Completed' : 'Pending'}
        type="completion"
      />
    ),
    sortable: true,
  },
  {
    id: 'publicVisible',
    label: 'Visibility',
    accessor: (reminder) => (
      <StatusBadge
        status={reminder.publicVisible ? 'Public' : 'Admin Only'}
        type="visibility"
      />
    ),
    sortable: true,
  },
  {
    id: 'createdBy',
    label: 'Created By',
    accessor: (reminder) => reminder.createdBy,
    sortable: true,
  },
  {
    id: 'actions',
    label: 'Actions',
    accessor: () => null, // Actions are handled by the AdminTable component
    sortable: false,
  },
];

// Reminder table configuration
export const reminderTableConfig = {
  entityName: 'Reminder',
  apiEndpoint: '/api/admin/reminders',
  columns: reminderColumns,
  searchFields: ['title', 'description', 'category', 'createdBy'] as (keyof Reminder)[],
  filterOptions: {
    priority: [
      { value: ReminderPriority.URGENT, label: 'Urgent' },
      { value: ReminderPriority.HIGH, label: 'High' },
      { value: ReminderPriority.MEDIUM, label: 'Medium' },
      { value: ReminderPriority.LOW, label: 'Low' }
    ],
    completed: [
      { value: 'true', label: 'Completed' },
      { value: 'false', label: 'Pending' }
    ],
    publicVisible: [
      { value: 'true', label: 'Public' },
      { value: 'false', label: 'Admin Only' }
    ],
    category: [] as { value: string; label: string }[] // Will be populated dynamically
  },
  exportFileName: 'reminders',
  defaultVisibleColumns: {
    title: true,
    dueDateTime: true,
    category: true,
    priority: true,
    completed: true,
    publicVisible: true,
    createdBy: true,
    actions: true
  }
};
